/**
 * DraftingSettingsDialog — the DSETTINGS drafting settings center (Faz
 * 11.13, OCS drafting_settings.rs parity): Snap & Grid, Polar Tracking,
 * Object Snap (with the marker color style + aperture), Dynamic Input and
 * Quick Properties in one tabbed dialog. Status-bar pill right-clicks and
 * the DSETTINGS command land on the relevant tab through
 * useDraftingSettingsStore.openAt.
 */

import { Box, Button, Checkbox, HStack, Input, Modal, ModalBody,
    ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay,
    Radio, RadioGroup, Tab, TabList, TabPanel, TabPanels, Tabs, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import {
    useDraftingSettingsStore, TAB_INDEX, type DraftingSettingsTab,
} from '../../state/useDraftingSettingsStore';
import { useSnapStore, type SnapType } from '../../tools/snapping/SnapManager';
import { useSystemVarsStore } from '../../state/useSystemVarsStore';
import { useAppStore } from '../../state/store';
import { useViewStore } from '../../state/commandAuxStores';

const SNAP_TYPE_ORDER: SnapType[] = [
    'endpoint', 'midpoint', 'center', 'node', 'quadrant',
    'intersection', 'apparentIntersection', 'extension', 'insertion',
    'perpendicular', 'tangent', 'geometricCenter', 'nearest', 'parallel',
];

const POLAR_ANGLES = [90, 45, 30, 22.5, 18, 15, 10, 5, 1];

/** One labeled numeric field row (value bound straight to a store write). */
function NumberRow({ label, value, min, max, onChange, step = 1 }: {
    label: string;
    value: number;
    min: number;
    max: number;
    onChange: (v: number) => void;
    step?: number;
}) {
    return (
        <HStack spacing={2} w="100%">
            <Text fontSize="xs" color="text.primary" flex="1">{label}</Text>
            <Input
                type="number"
                size="xs"
                w="90px"
                fontFamily="mono"
                value={value}
                min={min}
                max={max}
                step={step}
                onChange={(e) => {
                    const n = Number(e.target.value);
                    if (Number.isFinite(n)) onChange(Math.min(max, Math.max(min, n)));
                }}
            />
        </HStack>
    );
}

export function DraftingSettingsDialog() {
    const { t } = useTranslation();
    const open = useDraftingSettingsStore((s) => s.open);
    const tab = useDraftingSettingsStore((s) => s.tab);
    const close = useDraftingSettingsStore((s) => s.close);
    // Reactive field reads (the dialog re-renders on every toggle).
    const gridSnapEnabled = useSnapStore((s) => s.gridSnapEnabled);
    const gridSize = useSnapStore((s) => s.gridSize);
    const osnapEnabled = useSnapStore((s) => s.enabled);
    const snapTypes = useSnapStore((s) => s.types);
    const markerStyle = useSnapStore((s) => s.markerStyle);
    const aperture = useSnapStore((s) => s.aperture);
    const otrackOn = useSnapStore((s) => s.objectSnapTracking);
    const polarOn = useViewStore((s) => s.polarOn);
    const isoDrafting = useViewStore((s) => s.isoDrafting);
    const gridVisible = useAppStore((s) => s.view.gridVisible);
    const dynInput = useAppStore((s) => s.interactionOptions.dynamicInput);
    const quickPropsOpen = useAppStore((s) => s.quickPropertiesOpen);
    const polarang = useSystemVarsStore((s) => s.POLARANG);
    const polarmode = useSystemVarsStore((s) => s.POLARMODE);
    const clipromptlines = useSystemVarsStore((s) => s.CLIPROMPTLINES);
    const fadetime = useSystemVarsStore((s) => s.COMMANDLINEFADETIME);
    const cmdlineOpacity = useSystemVarsStore((s) => s.COMMANDLINEOPACITY);
    const pickbox = useSystemVarsStore((s) => s.PICKBOX);

    if (!open) return null;

    const snapActions = useSnapStore.getState();

    return (
        <Modal isOpen onClose={close} size="md">
            <ModalOverlay />
            <ModalContent bg="bg.panel" maxW="540px">
                <ModalHeader fontSize="sm">{t('dsettings.title')}</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    <Tabs
                        size="sm"
                        variant="enclosed"
                        index={TAB_INDEX[tab]}
                        onChange={(i) => {
                            const entry = (Object.entries(TAB_INDEX) as [DraftingSettingsTab, number][])
                                .find(([, idx]) => idx === i);
                            if (entry) useDraftingSettingsStore.setState({ tab: entry[0] });
                        }}
                    >
                        <TabList>
                            <Tab flexShrink={0} whiteSpace="nowrap">{t('dsettings.tab.snapGrid')}</Tab>
                            <Tab flexShrink={0} whiteSpace="nowrap">{t('dsettings.tab.polar')}</Tab>
                            <Tab flexShrink={0} whiteSpace="nowrap">{t('dsettings.tab.osnap')}</Tab>
                            <Tab flexShrink={0} whiteSpace="nowrap">{t('dsettings.tab.dyn')}</Tab>
                            <Tab flexShrink={0} whiteSpace="nowrap">{t('dsettings.tab.quickProps')}</Tab>
                        </TabList>
                        <TabPanels>
                            {/* ---- Snap & Grid ---- */}
                            <TabPanel>
                                <Box display="flex" flexDirection="column" gap={2}>
                                    <Checkbox
                                        size="sm"
                                        isChecked={gridSnapEnabled}
                                        onChange={(e) => snapActions.setGridSnapEnabled(e.target.checked)}
                                    >
                                        {t('dsettings.gridSnapOn')}
                                    </Checkbox>
                                    <Checkbox
                                        size="sm"
                                        isChecked={gridVisible}
                                        onChange={(e) => useAppStore.getState().updateView({ gridVisible: e.target.checked })}
                                    >
                                        {t('dsettings.gridVisible')}
                                    </Checkbox>
                                    <NumberRow
                                        label={t('dsettings.gridSpacing')}
                                        value={gridSize}
                                        min={0.001}
                                        max={10000}
                                        step={0.5}
                                        onChange={(v) => snapActions.setGridSize(v)}
                                    />
                                    <Checkbox
                                        size="sm"
                                        isChecked={isoDrafting}
                                        onChange={(e) => useViewStore.getState().set('isoDrafting', e.target.checked)}
                                    >
                                        {t('dsettings.isodraft')}
                                    </Checkbox>
                                </Box>
                            </TabPanel>
                            {/* ---- Polar Tracking ---- */}
                            <TabPanel>
                                <Box display="flex" flexDirection="column" gap={2}>
                                    <Checkbox
                                        size="sm"
                                        isChecked={polarOn}
                                        onChange={(e) => useViewStore.getState().set('polarOn', e.target.checked)}
                                    >
                                        {t('dsettings.polarOn')}
                                    </Checkbox>
                                    <Text fontSize="xs" color="text.secondary">{t('dsettings.polarAngle')}</Text>
                                    <RadioGroup
                                        value={String(polarang)}
                                        onChange={(v) => useSystemVarsStore.getState().set('POLARANG', Number(v))}
                                    >
                                        <Box display="flex" flexWrap="wrap" gap={2}>
                                            {POLAR_ANGLES.map((a) => (
                                                <Radio key={a} size="sm" value={String(a)}>
                                                    <Text fontSize="xs">{a}°</Text>
                                                </Radio>
                                            ))}
                                        </Box>
                                    </RadioGroup>
                                    <Checkbox
                                        size="sm"
                                        isChecked={polarmode === 1}
                                        onChange={(e) => useSystemVarsStore.getState().set('POLARMODE', e.target.checked ? 1 : 0)}
                                    >
                                        {t('dsettings.polarmode')}
                                    </Checkbox>
                                    <Checkbox
                                        size="sm"
                                        isChecked={otrackOn}
                                        onChange={(e) => {
                                            const s = useSnapStore.getState();
                                            if (s.objectSnapTracking !== e.target.checked) s.toggleObjectSnapTracking();
                                        }}
                                    >
                                        {t('dsettings.otrack')}
                                    </Checkbox>
                                </Box>
                            </TabPanel>
                            {/* ---- Object Snap ---- */}
                            <TabPanel>
                                <Box display="flex" flexDirection="column" gap={2}>
                                    <Checkbox
                                        size="sm"
                                        isChecked={osnapEnabled}
                                        onChange={(e) => snapActions.setEnabled(e.target.checked)}
                                    >
                                        {t('dsettings.osnapOn')}
                                    </Checkbox>
                                    <Box display="grid" gridTemplateColumns="1fr 1fr" gap={1}>
                                        {SNAP_TYPE_ORDER.map((type) => (
                                            <Checkbox
                                                key={type}
                                                size="sm"
                                                isChecked={snapTypes[type]}
                                                onChange={(e) => snapActions.setSnapType(type, e.target.checked)}
                                            >
                                                <Text fontSize="xs">{t(`statusbar.snapType.${type}`)}</Text>
                                            </Checkbox>
                                        ))}
                                    </Box>
                                    <Text fontSize="xs" color="text.secondary">{t('dsettings.markerStyle')}</Text>
                                    <RadioGroup
                                        value={markerStyle}
                                        onChange={(v) => useSnapStore.getState().setMarkerStyle(v as 'perType' | 'classicYellow')}
                                    >
                                        <Box display="flex" flexDirection="column" gap={1}>
                                            <Radio size="sm" value="perType">
                                                <Text fontSize="xs">{t('dsettings.markerPerType')}</Text>
                                            </Radio>
                                            <Radio size="sm" value="classicYellow">
                                                <Text fontSize="xs">{t('dsettings.markerClassicYellow')}</Text>
                                            </Radio>
                                        </Box>
                                    </RadioGroup>
                                    <NumberRow
                                        label={t('dsettings.aperture')}
                                        value={aperture}
                                        min={1}
                                        max={50}
                                        onChange={(v) => useSnapStore.getState().setAperture(v)}
                                    />
                                </Box>
                            </TabPanel>
                            {/* ---- Dynamic Input ---- */}
                            <TabPanel>
                                <Box display="flex" flexDirection="column" gap={2}>
                                    <Checkbox
                                        size="sm"
                                        isChecked={dynInput}
                                        onChange={(e) => useAppStore.getState().setInteractionOption('dynamicInput', e.target.checked)}
                                    >
                                        {t('dsettings.dynInput')}
                                    </Checkbox>
                                    <NumberRow
                                        label={t('dsettings.clipromptlines')}
                                        value={clipromptlines}
                                        min={0}
                                        max={50}
                                        onChange={(v) => useSystemVarsStore.getState().set('CLIPROMPTLINES', v)}
                                    />
                                    <NumberRow
                                        label={t('dsettings.cmdlinefadetime')}
                                        value={fadetime}
                                        min={0}
                                        max={60000}
                                        step={500}
                                        onChange={(v) => useSystemVarsStore.getState().set('COMMANDLINEFADETIME', v)}
                                    />
                                    <NumberRow
                                        label={t('dsettings.cmdlineopacity')}
                                        value={cmdlineOpacity}
                                        min={20}
                                        max={100}
                                        onChange={(v) => useSystemVarsStore.getState().set('COMMANDLINEOPACITY', v)}
                                    />
                                </Box>
                            </TabPanel>
                            {/* ---- Quick Properties ---- */}
                            <TabPanel>
                                <Box display="flex" flexDirection="column" gap={2}>
                                    <Checkbox
                                        size="sm"
                                        isChecked={quickPropsOpen}
                                        onChange={(e) => {
                                            const app = useAppStore.getState();
                                            if (app.quickPropertiesOpen !== e.target.checked) app.toggleQuickProperties();
                                        }}
                                    >
                                        {t('dsettings.quickPropsOn')}
                                    </Checkbox>
                                    <NumberRow
                                        label={t('dsettings.pickbox')}
                                        value={pickbox}
                                        min={0}
                                        max={50}
                                        onChange={(v) => useSystemVarsStore.getState().set('PICKBOX', v)}
                                    />
                                </Box>
                            </TabPanel>
                        </TabPanels>
                    </Tabs>
                </ModalBody>
                <ModalFooter>
                    <Button size="sm" variant="outline" borderColor="border.base" color="text.primary" onClick={close}>
                        {t('dsettings.close')}
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}

export default DraftingSettingsDialog;
